import React, { useMemo } from "react";
// @material-ui/core components
import {
  Box,
  Button,
  Dialog,
  makeStyles,
  Slide,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  useTheme,
} from "@material-ui/core";
// core components
import GridItem from "../../components/Grid/GridItem.js";
import GridContainer from "../../components/Grid/GridContainer.js";
import CustomInput from "../../components/CustomInput/CustomInput.js";
import Card from "../../components/Card/Card.js";
import CardHeader from "../../components/Card/CardHeader.js";
import CardBody from "../../components/Card/CardBody.js";
import _ from "lodash";
import {
  computeInvoiceTotals,
  applyExternalDiscountOnTaxable,
} from "../../utils/billInvoiceTotals";

const styles = {
  cardCategoryWhite: {
    color: "rgba(255,255,255,.62)",
    margin: "0",
    fontSize: "14px",
    marginTop: "0",
    marginBottom: "0",
  },
  cardTitleWhite: {
    color: "#FFFFFF",
    marginTop: "0px",
    minHeight: "auto",
    fontWeight: "300",
    fontFamily: "'Roboto', 'Helvetica', 'Arial', sans-serif",
    marginBottom: "3px",
    textDecoration: "none",
  },
};

const useStyles = makeStyles(styles);
const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const columns = [
  { id: "productCode", label: "Product Code" },
  { id: "productName", label: "Product Name" },
  { id: "lab", label: "Lab" },
  { id: "brand", label: "Brand" },
  { id: "price", label: "Price" },
];
export default function BillView(props) {
  const classes = useStyles();
  const theme = useTheme();
  const { open, close, item } = props;

  const { party, paymentStatus, challanCodes, items, code } = item;

  const challanSummary = useMemo(() => {
    if (!item) return null;
    const lineItems = item.items || [];
    const packagingCosts = item.packagingCosts || [];
    const isIgst = !!party?.isEligibleForIgst;
    if (lineItems.length === 0 && (packagingCosts || []).length === 0) {
      return null;
    }
    const pkgTaxAmt = (item.packagingCosts || []).reduce((sum, cost, i) => sum + (Number(cost) * Number(item.packagingTaxPercents?.[i] || 0)) / 100, 0);
    const inv = computeInvoiceTotals(lineItems, packagingCosts, isIgst, pkgTaxAmt);
    const discPct = Number(item.externalDiscountPercentage) || 0;
    const disc = applyExternalDiscountOnTaxable(inv, discPct, isIgst);
    const totalGstPre = isIgst ? inv.totalIgst : inv.totalCgst + inv.totalSgst;
    const totalGst = discPct > 0 ? disc.totalTaxAfter : totalGstPre;
    return {
      taxableValue: inv.taxableValue,
      discountedTaxable: disc.discountedTaxable,
      totalGst,
      totalQty: inv.totalQuantity,
      billAmount: disc.finalGrandTotal,
      discountPercent: discPct,
      discountAmount: disc.discountAmount,
    };
  }, [item]);

  return (
    <Dialog
      fullScreen
      open={open}
      onClose={close}
      TransitionComponent={Transition}
    >
      <GridContainer>
        <GridItem xs={12} sm={12}>
          <Card>
            <CardHeader color="primary">
              <Box
                display={"flex"}
                alignItems={"center"}
                justifyContent={"space-between"}
              >
                <h4 className={classes.cardTitleWhite}>Bill Details</h4>
                <Button style={{ color: "white" }} onClick={close}>
                  Close
                </Button>
              </Box>
            </CardHeader>
            <CardBody>
              <GridContainer>
                <GridItem xs={6} sm={6} md={6}>
                  <CustomInput
                    labelText="Party Name"
                    id="partyName"
                    formControlProps={{
                      fullWidth: true,
                    }}
                    inputProps={{
                      value: party?.accountName ? party.accountName : "",
                      disabled: true,
                    }}
                  />
                </GridItem>

                <GridItem xs={6} sm={6} md={6}>
                  <CustomInput
                    labelText="Payment Status"
                    id="paymentStatus"
                    formControlProps={{
                      fullWidth: true,
                    }}
                    inputProps={{
                      value: paymentStatus,
                      disabled: true,
                    }}
                  />
                </GridItem>
                <GridItem xs={12} sm={12} md={6}>
                  <CustomInput
                    labelText="Challan Codes"
                    id="challanCodes"
                    formControlProps={{
                      fullWidth: true,
                    }}
                    inputProps={{
                      value: challanCodes ? challanCodes.toString() : "",
                      disabled: true,
                    }}
                  />
                </GridItem>
                <GridItem xs={12} sm={12} md={6}>
                  <CustomInput
                    labelText="Code"
                    id="Code"
                    formControlProps={{
                      fullWidth: true,
                    }}
                    inputProps={{
                      value: code ? code.toString() : "",
                      disabled: true,
                    }}
                  />
                </GridItem>

                {challanSummary && (
                  <GridItem xs={12} sm={12} md={12}>
                    <Box
                      style={{
                        border: "1px solid #e0e0e0",
                        borderRadius: 4,
                        padding: 12,
                        marginTop: 8,
                      }}
                    >
                      <Typography variant="subtitle2" gutterBottom>
                        Selected challans summary
                      </Typography>
                      <Typography variant="body2">
                        Total challan (taxable): ₹
                        {Number(challanSummary.taxableValue).toFixed(2)}
                      </Typography>
                      <Typography variant="body2">
                        Total item qty: {challanSummary.totalQty}
                      </Typography>
                      {challanSummary.discountPercent > 0 && (
                        <>
                          <Typography variant="body2">
                            Discount @ {challanSummary.discountPercent}%: ₹
                            {Number(challanSummary.discountAmount).toFixed(2)}
                          </Typography>
                          <Typography variant="body2">
                            Sub total: ₹
                            {Number(challanSummary.discountedTaxable).toFixed(2)}
                          </Typography>
                        </>
                      )}
                      <Typography variant="body2">
                        Total GST: ₹
                        {Number(challanSummary.totalGst).toFixed(2)}
                      </Typography>
                      <Typography variant="body2" style={{ fontWeight: 600 }}>
                        Bill amount (payable): ₹
                        {Number(challanSummary.billAmount).toFixed(2)}
                      </Typography>
                    </Box>
                  </GridItem>
                )}

              </GridContainer>
            </CardBody>
          </Card>
        </GridItem>
      </GridContainer>
    </Dialog>
  );
}
